import type { ReportData } from 'teacher-ui-client/components/reporting/reading-eggspress/read-aloud-fluency/student/events/report-loader'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import Component from '@glimmer/component'
import { formatDate, t } from 'ember-intl'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { on } from '@ember/modifier'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
  type SortingConfig,
} from '@blakeelearning/data-tables/utils/sorting'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { PlayAndReviewButton } from 'teacher-ui-client/components/reporting/reading-eggspress/read-aloud-fluency/student/events/play-and-review-button'
import type Student from 'teacher-ui-client/models/student'

interface ReportSignature {
  Element: HTMLDivElement
  Args: {
    isReloading?: boolean
    reportData: ReportData
    student: Student
    reloadReport: () => void
  }
}

export class StudentEventsReport extends Component<ReportSignature> {
  @queryParam('reporting/ui-scope/rex/read-aloud-fluency/student') pageNumber!: number

  @queryParam('reporting/ui-scope/rex/read-aloud-fluency/student') perPage!: number

  <template>
    <div ...attributes>
      <table class="w-full table-auto">
        <thead>
          <tr class="bg-oceany-blue-100">
            <ThSort @sortKey="title" @text={{t "reporting.bookSlashChapter"}} class="max-w-64" />
            <ThSort @sortKey="created_at" @text={{t "reporting.dateSubmitted"}} />
            <ThSort @sortKey="lexile" @text={{t "reporting.lexileLevel"}} />
            <ThSort @sortKey="score" @text={{t "reporting.score"}} />
            <ThSort @sortKey="words_per_minute" @text={{t "reporting.wpm"}} />
            <th class="max-w-64 p-3">
              <span class="text-dusty-black-300 font-semibold">{{t "reporting.comments"}}</span>
            </th>
            <th class="w-px p-3">
              <span class="text-dusty-black-300 font-semibold">{{t "reporting.audio"}}</span>
            </th>
          </tr>
        </thead>
        <tbody class={{if @isReloading "opacity-50"}}>
          {{#each @reportData.events as |event index|}}
            <tr
              class="even:bg-oceany-blue-50 hover:bg-oceany-blue-150 divide-x divide-white odd:bg-white"
              data-test-table-row={{index}}
            >
              <td class="max-w-64 truncate p-3" data-test-cell="title">
                {{event.title}}
              </td>
              <td class="p-3" data-test-cell="created_at">
                {{formatDate event.created_at weekday="short" day="2-digit" month="short" year="2-digit"}}
              </td>
              <td class="p-3" data-test-cell="lexile_formatted">
                {{formatValue event.lexile_formatted}}
              </td>
              <td class="p-3" data-test-cell="score">
                {{formatValue event.score append=" / 5"}}
              </td>
              <td class="p-3" data-test-cell="words_per_minute">
                {{formatValue event.words_per_minute}}
              </td>
              <td class="max-w-64 truncate p-3" data-test-cell="comments">
                {{formatValue event.comments}}
              </td>
              <td class="py-1 pr-3" data-test-cell="play_and_review">
                <PlayAndReviewButton
                  @student={{@student}}
                  @assignmentId={{event.assignment_uuid}}
                  @reviewId={{event.review_id}}
                  @onSaved={{@reloadReport}}
                />
              </td>
            </tr>
          {{/each}}
        </tbody>
      </table>
    </div>
  </template>
}

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/rex/read-aloud-fluency/student') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig: SortingConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  updateSort = () => {
    this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span class="text-dusty-black-300 font-semibold">{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400 print:hidden" />
      </button>
    </th>
  </template>
}
